import React, { useState } from "react";
import { colors } from "../../../config/colors";
import { getJobTitleLabel, getChargeOutRateTitle, getCostOfSupplyTitle } from "../../../utils/jobTitleLabel";
import RenewRecurringModal from "./RenewRecurringModal";

const JobDetails = ({ job, onJobUpdated }) => {
    const jobLabel = getJobTitleLabel();
    const chargeOutRateTitle = getChargeOutRateTitle();
    const costOfSupplyTitle = getCostOfSupplyTitle();
    const [selectedDescription, setSelectedDescription] = useState(null);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
    const [showRenewModal, setShowRenewModal] = useState(false);

    const formatTime = (dateString) => {
        if (!dateString) return "Not scheduled";

        // Handle time-only strings like "20:01"
        if (typeof dateString === 'string' && dateString.match(/^\d{1,2}:\d{2}$/)) {
            const [hours, minutes] = dateString.split(':');
            const date = new Date();
            date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }

        // Handle full ISO date strings
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid date";
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            return "Invalid date";
        }
    };

    const DescriptionModal = ({ description, position, onMouseLeave }) => {
        return (
            <div
                className="absolute bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-[100] max-w-[500px]"
                style={{
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    transform: "translateY(-100%)",
                }}
                onMouseLeave={onMouseLeave}
            >
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {description}
                </p>
            </div>
        );
    };

    const handleMouseEnter = (description, event) => {
        const rect = event.target.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        setModalPosition({
            top: rect.top + scrollTop,
            left: rect.left,
        });
        setSelectedDescription(description);
    };

    const StatusBadge = ({ status }) => {
        const getStatusColor = () => {
            switch (status) {
                case 'COMPLETED':
                    return 'bg-green-100 text-green-800';
                case 'PENDING':
                    return 'bg-yellow-100 text-yellow-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
                {status === 'FAILED' ? 'Incomplete' : status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
            </span>
        );
    };

    const ExpiryBadge = ({ jobData }) => {
        const reoccurrence = jobData.reoccurrence;
        if (!reoccurrence || !reoccurrence.repeatTill) return null;

        const repeatTillDate = new Date(reoccurrence.repeatTill);
        if (isNaN(repeatTillDate.getTime())) return null;

        const daysUntilExpiry = Math.ceil((repeatTillDate - new Date()) / (1000 * 60 * 60 * 24));

        // Warning state ONLY shown on the very last job in the series
        const isExpiring = jobData.isLastRecurringJob && daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
        const isExpired = jobData.isLastRecurringJob && daysUntilExpiry < 0;

        if (isExpiring) {
            return (
                <div className="mt-2 flex items-center gap-3">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-orange-50 border border-orange-200 text-orange-800 text-xs font-medium whitespace-nowrap">
                        <span className="mr-1.5">⚠️</span>
                        Reoccurrence expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
                    </div>
                    <button
                        onClick={() => setShowRenewModal(true)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                        style={{ backgroundColor: colors.highlights.callToAction }}
                    >
                        Extend
                    </button>
                </div>
            );
        }

        if (isExpired) {
            return (
                <div className="mt-3 flex items-center gap-3">
                    <div className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 border border-gray-300 text-gray-700 text-sm font-medium">
                        <span className="mr-2">⏸️</span>
                        Reoccurrence expired {repeatTillDate.toLocaleDateString()}
                    </div>
                </div>
            );
        }

        return (
            <div className="mt-3 flex items-center gap-3">
                <div className="inline-flex items-center px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium">
                    <span className="mr-2">🔁</span>
                    Reoccurrence ends {repeatTillDate.toLocaleDateString()}
                </div>
            </div>
        );
    };

    const InfoCard = ({ title, children }) => (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all duration-200 hover:shadow-md">
            <h4 className="text-lg font-semibold mb-4" style={{ color: colors.primary.headingsBody }}>
                {title}
            </h4>
            {children}
        </div>
    );

    const DataRow = ({ label, value, isHighlight = false }) => (
        <div className="flex flex-col space-y-1">
            <span className="text-sm font-medium text-gray-500">{label}</span>
            <span className={`text-sm ${isHighlight ? 'font-semibold' : 'text-gray-700'}`}>{value}</span>
        </div>
    );

    return (
        <div className="space-y-6 animate-fadeIn">
            {showRenewModal && (
                <RenewRecurringModal
                    isOpen={showRenewModal}
                    onClose={() => setShowRenewModal(false)}
                    job={job}
                    onSuccess={() => {
                        if (onJobUpdated) onJobUpdated();
                    }}
                />
            )}
            <InfoCard title={`${jobLabel} Information`}>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <div>
                        <DataRow label={`${jobLabel} Title`} value={job.title} isHighlight={true} />
                        {job.reoccurrence && <ExpiryBadge jobData={job} />}
                    </div>
                    <div>
                        <span className="text-sm font-medium text-gray-500">Status</span>
                        <div className="mt-1">
                            <StatusBadge status={job.status} />
                        </div>
                    </div>
                    <div>
                        <DataRow
                            label="Category"
                            value={
                                job.categoryIds?.length
                                    ? job.categoryIds.map(c => c.categoryType).join(", ")
                                    : (job.categoryId?.length
                                        ? (Array.isArray(job.categoryId) ? job.categoryId.map(c => c.categoryType).join(", ") : (job.categoryId.categoryType || "Not Assigned"))
                                        : "Not Assigned")
                            }
                        />
                    </div>
                    <div>
                        <DataRow label="Approval Status" value={job.approved ? "✓ Approved" : "Pending Approval"} />
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-4">
                    <div>
                        <DataRow label="Date" value={job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString() : "Not scheduled"} />
                    </div>
                    <div>
                        <DataRow label="Estimated Start Time" value={formatTime(job.scheduledStartTime)} />
                    </div>
                    <div>
                        <DataRow label="Estimated End Time" value={formatTime(job.scheduledEndTime)} />
                    </div>
                </div>
            </InfoCard>

            <InfoCard title="Assigned Employee">
                {job.assignedEmployee ? (
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                <span className="text-xl text-gray-600">
                                    {job.assignedEmployee.name ? job.assignedEmployee.name.charAt(0) : "?"}
                                </span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h5 className="text-base font-medium">{job.assignedEmployee.name || "Not provided"}</h5>
                            <p className="text-sm text-gray-500">{job.assignedEmployee.email || "Not provided"}</p>
                            <p className="text-sm text-gray-500">{job.assignedEmployee.phone || "Not provided"}</p>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No employee assigned</p>
                )}
            </InfoCard>

            <InfoCard title="Customer Information">
                {job.customer ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <DataRow label="Name" value={job.customer.name || "Not provided"} />
                        <DataRow label="Phone" value={job.customer.phoneNumber || "Not provided"} />
                        <DataRow label="Email" value={job.customer.email || "Not provided"} />
                        <div className="col-span-2">
                            <DataRow
                                label="Address"
                                value={
                                    job.customer.street && job.customer.city
                                        ? `${job.customer.street}, ${job.customer.city}`
                                        : job.customer.street
                                            ? job.customer.street
                                            : job.customer.city
                                                ? job.customer.city
                                                : "Not provided"
                                }
                            />
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No customer information available</p>
                )}
            </InfoCard>

            <InfoCard title="Tasks">
                {selectedDescription && (
                    <DescriptionModal
                        description={selectedDescription}
                        position={modalPosition}
                        onMouseLeave={() => setSelectedDescription(null)}
                    />
                )}
                <div
                    className="overflow-x-auto"
                    onMouseLeave={() => setSelectedDescription(null)}
                >
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tl-lg">
                                    Task Title
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Description
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Quantity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    {chargeOutRateTitle}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tr-lg">
                                    {`Total ${chargeOutRateTitle}`}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {job.tasks.map((taskItem, index) => (
                                <tr
                                    key={taskItem.taskId}
                                    className={`transition-colors duration-150 hover:bg-gray-50 ${index === job.tasks.length - 1 ? 'rounded-b-lg' : ''
                                        }`}
                                    onMouseLeave={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setSelectedDescription(null);
                                    }}
                                >
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {taskItem.title}
                                    </td>
                                    <td
                                        className="px-6 py-4 text-sm text-gray-900 cursor-help"
                                        onMouseEnter={(e) =>
                                            handleMouseEnter(
                                                taskItem.description,
                                                e
                                            )
                                        }
                                        onMouseLeave={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setSelectedDescription(null);
                                        }}
                                    >
                                        <div className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                                            {taskItem.description}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {taskItem.quantity}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {(taskItem.cost && taskItem.cost > 0) ? `$${taskItem.cost}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                                        {(taskItem.cost && taskItem.cost > 0) ? `$${taskItem.cost * taskItem.quantity}` : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </InfoCard>

            <InfoCard title="Cost Summary">
                {job.cost ? (
                    <div className="bg-white rounded-xl overflow-hidden">
                        <div className={`grid grid-cols-1 md:grid-cols-${job.cost.costOfSupply !== undefined ? '4' : '3'} gap-0`}>
                            {job.cost.subtotal > 0 && (
                                <div className="p-6 relative">
                                    <div className="absolute top-0 right-0 w-px h-full bg-gray-100 md:block hidden"></div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-gray-500 font-medium">Subtotal</p>
                                        <p className="text-2xl font-semibold text-gray-700">${job.cost.subtotal}</p>
                                        <div className="flex items-center mt-2">
                                            <span className="text-xs text-gray-400">Base amount</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="p-6 relative bg-gray-50">
                                <div className="absolute top-0 right-0 w-px h-full bg-gray-100 md:block hidden"></div>
                                <div className="space-y-1">
                                    <p className="text-sm text-gray-500 font-medium">Tax Rate</p>
                                    <div className="flex items-baseline">
                                        <p className="text-2xl font-semibold text-gray-700">{job.cost.tax || 0}</p>
                                        <span className="text-lg ml-1 text-gray-500">%</span>
                                    </div>
                                    <div className="flex items-center mt-2">
                                        <span className="text-xs text-gray-400">Applied tax</span>
                                    </div>
                                </div>
                            </div>

                            {job.cost.costOfSupply !== undefined && (
                                <div className="p-6 relative bg-gray-50">
                                    <div className="absolute top-0 right-0 w-px h-full bg-gray-100 md:block hidden"></div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-gray-500 font-medium">{costOfSupplyTitle}</p>
                                        <div className="flex items-baseline">
                                            <p className="text-2xl font-semibold text-gray-700">${job.cost.costOfSupply}</p>
                                        </div>
                                        <div className="flex items-center mt-2">
                                            <span className="text-xs text-gray-400">Additional admin cost</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {job.cost.total > 0 && (
                                <div className="p-6" style={{ backgroundColor: `${colors.primary.headingsBody}15` }}>
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium" style={{ color: colors.primary.headingsBody }}>{`Total ${chargeOutRateTitle}`}</p>
                                        <p className="text-3xl font-bold" style={{ color: colors.primary.headingsBody }}>${job.cost.total}</p>
                                        <div className="flex items-center mt-2">
                                            <span className="text-xs" style={{ color: `${colors.primary.headingsBody}99` }}>Final amount (inc. tax)</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4">
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Tax is calculated based on your local tax rate</span>
                        </div>
                    </div> */}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No cost information available</p>
                )}
            </InfoCard>
        </div>
    );
};

export default JobDetails;
